/** One row of a `*_to_tag` join table: an item and a tag. */
export interface TagLink {
  tag: number;
  [parentField: string]: number;
}

/** The tag ids a given item carries. */
export function tagIdsFor(links: TagLink[] | undefined, parentField: string, id: number | null | undefined): number[] {
  if (id == null) return [];
  return Array.from(new Set((links ?? []).filter((l) => Number(l[parentField]) === Number(id)).map((l) => Number(l.tag))));
}

/**
 * Ids of the items in `links` that share at least one tag with `tagIds`, with the tags they share.
 * `selfId` is left out so an item never lists itself.
 */
export function itemsSharingTags(
  links: TagLink[] | undefined,
  parentField: string,
  tagIds: number[],
  selfId?: number | null,
): Map<number, number[]> {
  const wanted = new Set(tagIds.map(Number));
  const out = new Map<number, number[]>();
  for (const l of links ?? []) {
    const tag = Number(l.tag);
    const id = Number(l[parentField]);
    if (!wanted.has(tag) || !Number.isFinite(id) || (selfId != null && id === Number(selfId))) continue;
    const shared = out.get(id) ?? [];
    if (!shared.includes(tag)) shared.push(tag);
    out.set(id, shared);
  }
  return out;
}

interface ChildRow {
  id: number;
  go_to_market: number;
}

/**
 * The tags each architecture carries: its own, plus those on its use cases and solution components, because
 * those are the cards you see on the architecture's page. Keyed by go_to_market id.
 */
export function architectureTagIndex(args: {
  gtmLinks?: TagLink[];
  useCases?: ChildRow[];
  useCaseLinks?: TagLink[];
  components?: ChildRow[];
  componentLinks?: TagLink[];
}): Map<number, Set<number>> {
  const out = new Map<number, Set<number>>();
  const add = (gtm: number, tag: number) => {
    if (!Number.isFinite(gtm) || !Number.isFinite(tag)) return;
    const set = out.get(gtm) ?? new Set<number>();
    set.add(tag);
    out.set(gtm, set);
  };

  for (const l of args.gtmLinks ?? []) add(Number(l.go_to_market), Number(l.tag));

  const gtmOf = (rows: ChildRow[] | undefined) => new Map((rows ?? []).map((r) => [Number(r.id), Number(r.go_to_market)]));
  const useCaseGtm = gtmOf(args.useCases);
  for (const l of args.useCaseLinks ?? []) {
    const gtm = useCaseGtm.get(Number(l.security_use_case));
    if (gtm != null) add(gtm, Number(l.tag));
  }
  const componentGtm = gtmOf(args.components);
  for (const l of args.componentLinks ?? []) {
    const gtm = componentGtm.get(Number(l.solution_component));
    if (gtm != null) add(gtm, Number(l.tag));
  }
  return out;
}
